import '../IntegrationsTab.scss'
import React, { useEffect, useMemo, useState } from 'react'
import { Col, Container, OverlayTrigger, Row, Stack, Tooltip } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { ZoomSettingsModal } from './ZoomSettingsModal'
import { useGetMeQuery, useGetUserIntegrationInfoQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { TooltipProps } from 'react-bootstrap/Tooltip'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { Modal } from '../../../../components/Modal/Modal'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'
import { getIntegrationRedirectUri } from '../../../Integration/utils'

export const ZoomIntegrationRow = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectAuthenticatedUser)
  const [showZoomSettingsModal, setShowSettingsModal] = useState(false)
  const { data: userData, refetch: refetchUserData } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { data: userIntegrationInfoData } = useGetUserIntegrationInfoQuery({
    accountId,
    userId: currentUser!.id,
  })
  const zoomIntegration = useMemo(() => {
    const user = userData
    if (!user || !user.integrations) {
      return null
    }
    return user.integrations.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
  }, [userData])

  const [showProblemsTooltip, setShowProblemsTooltip] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)

  useEffect(() => {
    if (!userIntegrationInfoData?.data.info.length || userIntegrationInfoData?.data.problems.length) {
      return
    }
    const missingFeatures = userIntegrationInfoData.data.info.some((integrationInfo) => integrationInfo.issue.startsWith('Missing features'))
    if (missingFeatures) {
      setShowUpdateModal(true)
    }
  }, [userIntegrationInfoData])

  const handleHideZoomSettings = () => {
    setShowSettingsModal(false)
  }

  const handleShowSettingsModal = async () => {
    await refetchUserData()
    setShowSettingsModal(true)
  }

  const authorizeZoom = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.ZOOM)
    const state = createState(window.location.origin + window.location.pathname + '?app=meeting')
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.ZOOM}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const uninstallZoom = () => {
    window.open('https://marketplace.zoom.us/user/installed', '_blank', 'noopener noreferrer')
  }

  const zoomIntegrationProblems = zoomIntegration
    ? (userIntegrationInfoData?.data.problems.filter((issue) => issue.integrationId === zoomIntegration.id) ?? [])
    : []

  const renderZoomIntegrationProblemsTooltip = (props: TooltipProps) => (
    <Tooltip
      id='integration-info-tooltip'
      className='integration-info-tooltip'
      {...props}
      onMouseEnter={() => setShowProblemsTooltip(true)}
      onMouseLeave={() => setShowProblemsTooltip(false)}
    >
      <ul>
        {zoomIntegrationProblems.map((problem) => {
          return (
            <>
              <li className='mb-2'>
                <b>{problem.issue}</b> {problem.resolution}
              </li>
            </>
          )
        })}
      </ul>
    </Tooltip>
  )

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      <ZoomSettingsModal show={showZoomSettingsModal} onHide={handleHideZoomSettings} />
      <MissingFeaturesModal show={showUpdateModal} onAuthorizeZoom={authorizeZoom} onHide={() => setShowUpdateModal(false)} />

      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.ZOOM} width={48} height={48} />
            </div>
            <div>
              <h6>Zoom</h6>
              <div>Connect your Zocks account to Zoom.</div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            {zoomIntegration && (
              <div className='d-flex gap-3'>
                {zoomIntegrationProblems.length ? (
                  <OverlayTrigger show={showProblemsTooltip} placement='top' overlay={renderZoomIntegrationProblemsTooltip}>
                    <div className='align-self-center' onMouseEnter={() => setShowProblemsTooltip(true)} onMouseLeave={() => setShowProblemsTooltip(false)}>
                      <i className='bi bi-exclamation-triangle zocks-text-warning-text' />
                    </div>
                  </OverlayTrigger>
                ) : null}
                <Button variant='primaryGhost' onClick={handleShowSettingsModal}>
                  <i className='bi bi-gear' />
                </Button>
              </div>
            )}
            <ScopedBlock
              scope={{
                scope: 'integration:connect',
                superScope: ['integration:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <div className='connect-button-container'>
                {zoomIntegration ? (
                  <div className='connect-button-container'>
                    <Button variant='primary' onClick={uninstallZoom}>
                      Disconnect
                    </Button>
                    <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                      <i className='bi bi-check' />
                      Connected
                    </div>
                  </div>
                ) : (
                  <ScopedBlock
                    scope={{
                      scope: 'room_session:start',
                      superScope: ['room_session:admin', 'room:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Button variant='primary' onClick={authorizeZoom}>
                      Connect
                    </Button>
                  </ScopedBlock>
                )}
              </div>
            </ScopedBlock>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}

interface MissingFeaturesModalProps {
  show: boolean
  onAuthorizeZoom: () => void
  onHide: () => void
}

const MissingFeaturesModal: React.FC<MissingFeaturesModalProps> = ({ show, onAuthorizeZoom, onHide }) => {
  return (
    <Modal
      show={show}
      header={<h4>Zoom connection needs updating</h4>}
      body={
        <div className='pt-4'>
          To use the{' '}
          <a
            className='button-link-medium paragraph-medium text-decoration-none zocks-text-primary fw-normal'
            href='https://marketplace.zoom.us/apps/an_KQeraSPyMEKowV9KDvQ'
            target='_blank'
            rel='noreferrer noopeener'
          >
            Zocks app in Zoom
          </a>{' '}
          meetings, you need to reconnect your Zoom account to Zocks.
        </div>
      }
      footerPrimaryActionTitle='Reconnect Zoom'
      footerClassName='p-4'
      showFooterActions
      centered
      onHide={onHide}
      onFooterPrimaryAction={onAuthorizeZoom}
      onFooterSecondaryAction={onHide}
    />
  )
}
